import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, API } from "@/lib/apiClient";
import { StatusPill } from "@/components/StatusPill";
import DocumentSheet from "@/components/DocumentSheet";
import ChatThread from "@/components/ChatThread";
import { useCommentsPoll } from "@/lib/useCommentsPoll";
import { useScrollToHash } from "@/lib/useScrollToHash";
import FileRow from "@/components/FileRow";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Download, MessageSquare, Lock, IndianRupee, Edit3, Trash2, Upload, Receipt, FileText, Copy } from "lucide-react";
import { prettyType } from "@/lib/serviceGroups";
import TermsAgreement from "@/components/TermsAgreement";
import DocumentPasswordNotice from "@/components/DocumentPasswordNotice";
import { TERMS_VERSION } from "@/lib/terms";
import { oneLine } from "@/lib/address";

export default function TransactionDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [t, setT] = useState(null);
  const [comments, setComments] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [addresses, setAddresses] = useState([]);
  // Unselected by default when there's a real choice to make — see the same reasoning in CartPage.
  const [addrIdx, setAddrIdx] = useState(null);
  // See the note in CartPage: consent is an act at the moment of paying, never a remembered setting.
  const [agreed, setAgreed] = useState(false);
  const [mobile, setMobile] = useState("");
  const [docView, setDocView] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [docBusy, setDocBusy] = useState(false);

  const load = () => {
    api.get(`/transactions/${id}`).then(r => setT(r.data)).catch(() => {});
    api.get(`/transactions/${id}/comments`).then(r => setComments(r.data)).catch(() => {});
    api.get("/auth/me").then(r => {
      const addrs = r.data.addresses || [];
      setAddresses(addrs);
      setMobile(r.data.mobile || "");
      setAddrIdx(prev => (prev === null && addrs.length === 1 ? 0 : prev));
    });
  };
  useEffect(load, [id]);
  // Live thread: without this, a reply from admin or the reviewer only appeared after the
  // customer manually reloaded the page.
  useCommentsPoll(id, setComments);
  // Lands on the Messages card, not the top of the page, when arrived at via the dashboard's
  // message-count icon.
  useScrollToHash(!!t);
  // Marks this case read so the dashboard's "N from DharmaTN" badge (an unread count, not a
  // running total — see _attach_customer_message_counts) clears once the customer has actually
  // seen the reply, not merely posted their own message.
  useEffect(() => {
    if (!id) return;
    api.post(`/transactions/${id}/viewed`).catch(() => {});
  }, [id]);

  // Fetched on click, not on page load. Re-fetched each time so the customer always sees the
  // current text — admin may have edited it since they last looked.
  const openDoc = async () => {
    setDocOpen(true);
    setDocBusy(true);
    try {
      const { data } = await api.get(`/transactions/${id}/document-view`);
      setDocView(data);
    } catch { toast.error("Could not load your document"); }
    finally { setDocBusy(false); }
  };

  const post = async () => {
    if (!text.trim()) return;
    try { await api.post(`/transactions/${id}/comments`, { text }); setText(""); load(); toast.success("Comment sent"); }
    catch { toast.error("Send failed"); }
  };
  const pay = async () => {
    try {
      await api.post(`/transactions/${id}/pay`,
                     { address_index: addrIdx, terms_accepted: agreed, terms_version: TERMS_VERSION });
      toast.success("Payment recorded");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Payment failed"); }
  };
  const copyId = async () => {
    try { await navigator.clipboard.writeText(id); toast.success("Transaction ID copied"); }
    catch { toast.error("Could not copy — clipboard unavailable"); }
  };
  const del = async () => {
    if (!window.confirm("Delete this transaction?")) return;
    try { await api.delete(`/transactions/${id}`); toast.success("Deleted"); nav("/customer"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const upload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("kind", "document");
      await api.post(`/transactions/${id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded"); setFile(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  if (!t) return <div>Loading...</div>;
  const locked = t.status === "closed";
  const paymentLocked = ["completed", "refunded"].includes(t.payment_status);
  // "Review completed" is the point the document is cleared for issue. Before it, a correction is
  // cheap and we actively invite one; after it, the document is heading for stamp paper.
  const reviewAccepted = t.review_status === "accepted";
  // Payment preconditions — mirrors core.lifecycle.require_customer_info, which the server
  // enforces on POST /transactions/{tid}/pay. The button is disabled rather than left to 400.
  const payMissing = [
    ...(mobile.trim() ? [] : ["mobile number"]),
    ...(addresses.length ? [] : ["delivery address"]),
  ];
  const mustPickAddress = addresses.length > 1 && addrIdx === null;
  const canPay = payMissing.length === 0 && !mustPickAddress && agreed;
  const editable = !paymentLocked && (t.service_type === "rental_agreement" || t.service_type === "affidavit");
  const deletable = !paymentLocked;
  const tok = localStorage.getItem("dt_token");
  const advocateReports = (t.reports || []).filter(r => r.kind !== "softcopy");
  // Only the current copy is offered — older generations are superseded. Listing every version
  // risked the customer printing and signing a stale draft (LIFECYCLE.md "Current soft copy").
  const allSoftcopies = (t.reports || []).filter(r => r.kind === "softcopy");
  const softcopyReports = t.current_softcopy_id
    ? allSoftcopies.filter(r => r.id === t.current_softcopy_id)
    : allSoftcopies.slice(-1);
  // The template's title names the document; `subtype` is an internal key and rendering it
  // showed customers things like "Affidavit · one and same person".
  // The template's title names the document; falls back to the broad type rather than to
  // `service_type` itself, which would print a raw key like "contract_review" at the customer.
  const svc = t.template_title || prettyType(t.service_type);

  return (
    <div className="space-y-6" data-testid="txn-detail">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          {/* The eight characters on screen are not the id. Support asks for the whole thing, and
              reading a UUID off a phone by hand is how transcription errors happen — so the case
              page offers the same copy control the tables do. */}
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#CBA96C] flex items-center gap-2">
            Transaction {t.id.slice(0,8)}
            <button
              data-testid="copy-txn-id" onClick={copyId}
              title={`Copy full ID — ${t.id}`} aria-label="Copy transaction ID"
              className="text-[#6B7280] hover:text-[#CBA96C] transition-colors normal-case tracking-normal"
            ><Copy className="w-3 h-3"/></button>
          </div>
          {/* The document's name leads, with its type beneath — they came here looking for
              "Rental agreement for Rajam", not for a category. `display_name` is the customer's
              own name when they gave one and "Contract-e7b69dce" when they didn't, so the
              heading always names *this* document rather than the kind of thing it is. */}
          <h1 className="font-serif-display text-2xl md:text-4xl text-white mt-1 break-words">{t.display_name || svc}</h1>
          {t.display_name !== svc && <div className="text-sm text-[#A0AABF] mt-1">{svc}</div>}
        </div>
        <div className="flex items-center gap-2">
          {/* The soft copy — the document itself, not a record of paying for it — sits right next
              to the bill for the same reason the bill moved up here: it is what a customer comes
              back to this page looking for, not something worth a scroll down to Documents &
              Reports to find (which no longer repeats it). Only the current copy is ever offered
              (see softcopyReports above) — always visible, but disabled until admin has issued
              one, rather than appearing out of nowhere once it exists. */}
          {softcopyReports[0] ? (
            <a href={`${API}/files/${softcopyReports[0].id}?_t=${tok}`} target="_blank" rel="noopener noreferrer"
               data-testid="dl-softcopy-txn" title="Download soft copy" aria-label="Download soft copy">
              <Button variant="outline" size="sm" className="bg-transparent border-white/15 text-white hover:bg-white/5">
                <FileText className="w-3.5 h-3.5 sm:mr-1.5"/><span className="hidden sm:inline">Soft copy</span>
              </Button>
            </a>
          ) : (
            <Button variant="outline" size="sm" disabled data-testid="dl-softcopy-txn"
              title="Not issued yet — the admin will upload this once your case is ready"
              aria-label="Soft copy not issued yet"
              className="bg-transparent border-white/10 text-[#6B7280] cursor-not-allowed">
              <FileText className="w-3.5 h-3.5 sm:mr-1.5"/><span className="hidden sm:inline">Soft copy</span>
            </Button>
          )}
          {/* The tax invoice, where a receipt is normally kept — at the top of the thing it is a
              receipt *for*. It used to sit at the bottom of Documents & Reports, below the
              uploads and the soft copy, which is neither where anyone looks for a bill nor what
              that card is about. Only once the order exists: there is nothing to invoice before. */}
          {t.payment_status === "completed" && t.order_id && (
            <a href={`${API}/orders/${t.order_id}/bill?_t=${tok}`} target="_blank" rel="noopener noreferrer"
               data-testid="dl-bill-txn" title="Download tax invoice" aria-label="Download tax invoice">
              <Button variant="outline" size="sm" className="bg-transparent border-white/15 text-white hover:bg-white/5">
                <Receipt className="w-3.5 h-3.5 sm:mr-1.5"/><span className="hidden sm:inline">Bill</span>
              </Button>
            </a>
          )}
          {editable && (
            <Link to={`/customer/wizard/${t.service_type === "affidavit" ? "affidavit?type=" + t.subtype + "&txn=" + t.id : t.service_type + "?txn=" + t.id}`}>
              <Button data-testid="edit-txn" variant="outline" size="sm" className="bg-transparent border-white/15 text-white hover:bg-white/5">
                <Edit3 className="w-3.5 h-3.5 mr-1.5"/> Edit
              </Button>
            </Link>
          )}
          {deletable && (
            <Button data-testid="del-txn" onClick={del} variant="outline" size="sm" className="bg-transparent border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10">
              <Trash2 className="w-3.5 h-3.5 mr-1.5"/> Delete
            </Button>
          )}
          {/* No back control here: CustomerLayout renders "< Back" above this heading on every
              page below the dashboard. This was a second one, and the only icon-only version of
              it in the app. */}
        </div>
      </div>

      {/* Status strip — mirrors the dashboard's Transactions table so the two views read the same */}
      <div className="bg-[#111623] border border-white/10 rounded-2xl overflow-x-auto">
        <div className="grid grid-cols-4 gap-3 px-5 py-3 border-b border-white/5 text-[10px] tracking-[0.24em] uppercase text-[#6B7280] min-w-[420px]">
          <div>Status</div><div>Payment</div><div>Soft Copy</div><div>Delivery</div>
        </div>
        <div className="grid grid-cols-4 gap-3 px-5 py-4 items-center min-w-[420px]">
          <div><StatusPill status={t.status} dim="status"/></div>
          <div><StatusPill status={t.payment_status} dim="payment_status"/></div>
          <div><StatusPill status={t.softcopy_status} dim="softcopy_status"/></div>
          <div><StatusPill status={t.delivery_status} dim="delivery_status"/></div>
        </div>
      </div>

      {/* This one IS a gate, and the customer is the only party who can clear it — admin has no
          override (see LIFECYCLE.md "Fulfillment follows customer contact details"). So unlike
          the proofing prompt below, saying nothing here would strand the case indefinitely:
          their document cannot be issued or couriered until they fill these in. */}
      {t.status === "submitted" && (t.customer_info_missing || []).length > 0 && (
        <Card className="border-[#F27373]/50" data-testid="customer-info-missing-panel">
          <CardHeader>
            <CardTitle className="font-serif-display text-lg">We need your contact details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-[#A0AABF]">
              We can't prepare or deliver your document until you add your{" "}
              <strong>
                {(t.customer_info_missing || [])
                  .map(f => ({ mobile: "mobile number", address: "delivery address",
                               email: "email address", name: "full name" }[f] || f))
                  .join(" and ")}
              </strong>. Your case is on hold until then.
            </p>
            <Link
              to="/customer/profile"
              data-testid="fix-customer-info"
              className="inline-flex items-center rounded-full bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190] text-xs px-5 h-9 leading-9"
            >Add my details</Link>
          </CardContent>
        </Card>
      )}

      {/* Two states, split at "review completed" — the point after which the document is cleared
          for issue and changing it stops being reasonable.

          This used to key off `softcopy_status === "available"`, which was exactly backwards: the
          soft copy cannot be issued until the review is accepted (core.lifecycle
          .require_review_accepted), so the invitation to "tell us straight away" only ever
          appeared *after* the window for changes had closed. The prompt now runs from payment
          until the review is accepted, which is the whole period during which a correction is
          cheap. It remains a prompt, not a gate — dispatch is never blocked on the customer
          answering (LIFECYCLE.md "Why there is no customer approval gate"). */}
      {paymentLocked && !locked && (reviewAccepted ? (
        <Card className="border-[#6B7280]/40" data-testid="document-final-panel">
          <CardHeader>
            <CardTitle className="font-serif-display text-lg">Your document is ready for print</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            <p className="text-[#A0AABF]">
              Your document has completed legal review and is now being prepared for printing on stamp
              paper. Changes are no longer accepted at this stage.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#CBA96C]/50" data-testid="check-document-panel">
          <CardHeader>
            <CardTitle className="font-serif-display text-lg">Please check your document now</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <p className="text-[#A0AABF]">
              Open <strong>View document</strong> below and check it carefully — especially the spelling
              of names, parentage and any dates. If anything is wrong,{" "}
              <button
                data-testid="report-problem"
                onClick={() => document.getElementById("txn-comment-box")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="text-white underline underline-offset-2 hover:opacity-80 font-medium"
              >tell us straight away</button>{" "}
              and we'll correct it. Once our advocate completes the legal review, the document is
              cleared for printing and changes are no longer accepted.
            </p>
          </CardContent>
        </Card>
      ))}

      {/* The payment lock is permanent by design — corrections travel as messages, and admin
          applies them. See LIFECYCLE.md "Who can edit the document, and when". Silently removing
          the Edit button isn't enough; the customer has to be told where to ask. */}
      {paymentLocked && !locked && (
        <div className="rounded-xl border border-white/10 bg-[#111623] px-4 py-3 text-sm" data-testid="locked-notice">
          <div className="flex items-start gap-2 text-[#A0AABF]">
            <Lock className="w-3.5 h-3.5 mt-0.5 shrink-0"/>
            <div>
              Your details are locked now that payment is complete.{" "}
              <button
                data-testid="request-correction"
                onClick={() => document.getElementById("txn-comment-box")?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className="text-[#CBA96C] underline underline-offset-2 hover:opacity-80"
              >Message us below</button>{" "}
              if anything needs correcting and we'll update it for you.
            </div>
          </div>
        </div>
      )}
      {locked && (
        <div className="text-sm text-[#6B7280]" data-testid="closed-notice">
          <div className="inline-flex items-center gap-2"><Lock className="w-3.5 h-3.5"/> This case is closed — its details can no longer be changed.</div>
          {/* closing_note is a mandatory field on the close action (LIFECYCLE.md "Closing a
              case") and was already on this payload — admin just had no reason to say why to
              the one person who couldn't see it. Shown as a direct quote, not paraphrased, so
              it can't be misread as this app's own explanation rather than admin's own words. */}
          {t.closing_note && (
            <div className="mt-1" data-testid="closing-note">Admin closed this case with a note: “{t.closing_note}”</div>
          )}
        </div>
      )}

      <div>
        {/* One card, not two — "Submission Summary" used to be its own card holding only a
            field-by-field dump (redundant with the document itself, now removed) and the "View
            document" button; folding it in here means one place for everything about the
            document itself, rather than a nearly-empty card next to the one that matters. */}
        <Card>
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
            <CardTitle className="font-serif-display text-lg">Document & Report</CardTitle>
            {/* On demand only — mirrors admin's "View / Edit document", minus the edit. The
                document is fetched when asked for rather than on every page load, so a customer
                who only wants their status doesn't pull the whole document text. */}
            {/* Plain `variant="outline"` — these Cards are white (--card: 0 0% 100%), so the
                dark-theme button styling used elsewhere on this page renders invisible here. */}
            <Button data-testid="open-doc-view" variant="outline" size="sm" onClick={openDoc}>
              <FileText className="w-3.5 h-3.5 mr-1.5"/>View document
            </Button>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {/* The customer is told the password *before* they open a file, not after they have
                been refused by a reader. The case's own pinned number comes first and the profile
                is only the fallback for cases older than that field: documents are locked at serve
                time, so a customer who changed their mobile would otherwise be shown a number that
                opens nothing they had already downloaded. Same value the lock is derived from
                (`customer_pdf_passwords`), so the notice and the password cannot disagree. */}
            <DocumentPasswordNotice mobile={t.document_password_mobile || mobile} />
            <div>
              {/* Split by who attached it — this list used to be titled "Your uploads" outright,
                  which stopped being true the moment admin gained the ability to upload something
                  here too (backend/CLAUDE.md "Admin/reviewer can attach a document for the
                  customer"): a form to fill or a draft to review would have appeared under a
                  heading that said it was the customer's own. */}
              {(() => {
                const mine = (t.documents || []).filter(f => f.uploader_role === "customer");
                const fromUs = (t.documents || []).filter(f => f.uploader_role !== "customer");
                return (
                  <>
                    <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-2">Your uploads</div>
                    {mine.length === 0 && <div className="text-[#6B7280]">No uploads.</div>}
                    {mine.map(f => (
                      <FileRow key={f.id} file={f} locked={locked} onDeleted={load} className="hover:text-white"/>
                    ))}
                    {/* The upload control belongs to *this* section — it adds to "Your uploads",
                        not to what DharmaTN attached — so it has to sit here, above "From
                        DharmaTN" below, not trail after both lists where it read as belonging to
                        neither. */}
                    {/* Uploading closes at "review completed", not at close: once the advocate's
                        review is accepted the document is cleared for printing, so a late upload
                        can no longer influence it and offering the control would imply otherwise.
                        Enforced server-side too (routers/shared.py upload_file) — downloads stay
                        available either way. */}
                    {/* Stacked on a phone: a file input renders at the browser's own idea of a
                        width ("Choose file · no file selected"), which does not shrink below
                        roughly 240px and shoved the Upload button off the card. */}
                    {!locked && !reviewAccepted && (
                      <div className="flex flex-col gap-2 mt-2 sm:flex-row sm:items-center">
                        <input data-testid="upload-doc-input" type="file" accept=".pdf,.jpg,.jpeg,.gif,.doc,.docx,.rtf,.txt,.zip" onChange={e => setFile(e.target.files[0])} className="text-xs w-full min-w-0 sm:flex-1"/>
                        <Button data-testid="upload-doc-btn" size="sm" variant="outline" disabled={!file || uploading} onClick={upload} className="self-start sm:self-auto shrink-0">
                          <Upload className="w-3.5 h-3.5 mr-1.5"/>{uploading ? "Uploading…" : "Upload"}
                        </Button>
                      </div>
                    )}
                    {!locked && reviewAccepted && (
                      <div data-testid="upload-closed-notice" className="mt-2 flex items-start gap-1.5 text-xs text-[#6B7280]">
                        <Lock className="w-3 h-3 mt-0.5 shrink-0"/>
                        <span>Legal review is complete — documents can no longer be added. Message us on the case if something needs attention.</span>
                      </div>
                    )}
                    {fromUs.length > 0 && (
                      <>
                        <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-2 mt-3 pt-3 border-t">From DharmaTN</div>
                        {fromUs.map(f => (
                          <FileRow key={f.id} file={f} locked={locked} onDeleted={load} className="hover:text-white"/>
                        ))}
                      </>
                    )}
                  </>
                );
              })()}
            </div>
            {/* Hidden until there is actually something to show — a reviewer's report is
                internal by default and only ever appears here once admin shares it
                (backend/CLAUDE.md "Artifact visibility"), so "Awaiting report" sat under this
                heading on nearly every case, reading as a promise ("one is coming") the reviewer
                may never write and the customer never needed to see either way. */}
            {advocateReports.length > 0 && (
              <div className="border-t pt-3">
                <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-2">Advocate uploads</div>
                {advocateReports.map(f => (
                  <a key={f.id} href={`${API}/files/${f.id}`} data-testid={`download-report-${f.id}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-between gap-2 py-1.5 hover:text-white"><span className="min-w-0 truncate" title={f.name}>{f.name}</span><Download className="w-4 h-4 shrink-0"/></a>
                ))}
              </div>
            )}
            {t.delivery_status && t.delivery_status !== "not_started" && (
              <div className="border-t pt-3">
                <div className="text-xs uppercase text-[#6B7280] tracking-widest mb-2">Delivery</div>
                {t.courier_name && <div className="break-words">Courier: {t.courier_name}</div>}
                {/* Tracking ids and courier URLs are long unbroken strings — the two things most
                    likely to push this card wider than a phone. */}
                {t.tracking_id && <div className="text-[#6B7280] break-all">Tracking ID: {t.tracking_id}</div>}
                {t.tracking_url && <a href={t.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[#CBA96C] hover:underline break-all">Track shipment →</a>}
              </div>
            )}
            {t.payment_status === "failed" && (
              <div className="text-sm text-[#F27373]">Your last payment attempt failed. Please try again.</div>
            )}
            {!paymentLocked && (
              <>
                {/* Paying here bypasses the cart, so the address choice the cart offers has to
                    exist on this path too — otherwise a direct payment records no address at all
                    (see LIFECYCLE.md "Known defects"). Mirrors core.lifecycle.require_customer_info. */}
                {payMissing.length > 0 && (
                  <div data-testid="pay-info-missing" className="mt-3 p-3 rounded border border-[#F27373]/30 bg-[#F27373]/10 text-xs text-[#F27373] space-y-2">
                    <div>We need your <strong>{payMissing.join(" and ")}</strong> before you can pay — your document is couriered to you.</div>
                    <Link to="/customer/profile" data-testid="pay-fix-info" className="underline font-medium block">Add my details →</Link>
                  </div>
                )}
                {addresses.length > 0 && (
                  <div className="mt-3">
                    <div className="text-xs uppercase tracking-widest text-[#6B7280] mb-1.5">Deliver to</div>
                    {addresses.map((a, i) => (
                      <label key={i} className={`flex gap-2 items-start p-2 border rounded cursor-pointer mb-1 ${addrIdx === i ? "border-[#CBA96C] bg-[#CBA96C]/15" : "border-white/10 hover:bg-white/5"}`}>
                        <input type="radio" name="pay-addr" checked={addrIdx === i} onChange={() => setAddrIdx(i)} data-testid={`pay-addr-${i}`}/>
                        <div><div className="font-medium text-xs text-white">{a.label || `Address ${i + 1}`}</div><div className="text-xs text-[#6B7280]">{oneLine(a)}</div></div>
                      </label>
                    ))}
                    {mustPickAddress && <div data-testid="pay-pick-address" className="text-xs text-[#DBA94C] mt-1">Choose an address before paying.</div>}
                  </div>
                )}
                <TermsAgreement checked={agreed} onChange={setAgreed} testId="pay-terms-agree" />
                <Button data-testid="pay-btn" disabled={!canPay} onClick={pay} className="w-full mt-3 bg-[#CBA96C] text-white hover:bg-[#b8965a] disabled:opacity-40"><IndianRupee className="w-4 h-4 mr-1"/>Pay ₹{t.price} (Mock)</Button>
              </>
            )}
            {/* The bill lives in the page header now, not here. This card is about the *document*
                — what the customer and admin have attached to the case — and a tax invoice is a
                record of the purchase, not part of the legal work. */}
          </CardContent>
        </Card>
      </div>

      <Card id="case-messages">
        <CardHeader><CardTitle className="font-serif-display text-lg flex items-center gap-2"><MessageSquare className="w-4 h-4"/> Messages</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="max-h-72 overflow-y-auto">
            {/* Customer, reviewer and admin share one thread (LIFECYCLE.md "Message and
                audit-log visibility") — labelFor says who the reviewer is without a stranger
                appearing unexplained in the customer's conversation, and keeps which specific
                advocate reviewed the case unnamed to the customer. */}
            <ChatThread comments={comments} viewerRole="customer"
                        labelFor={(c) => c.author_role === "reviewer" ? "Legal Reviewer (shared by our team)" : undefined}/>
          </div>
          {/* A closed case is sealed — see LIFECYCLE.md "Terminal cases are sealed" */}
          {locked ? (
            <div className="text-sm text-[#6B7280] inline-flex items-center gap-2" data-testid="thread-sealed">
              <Lock className="w-3.5 h-3.5"/> This case is closed. Please start a new request if you need anything further.
            </div>
          ) : (
            <div className="flex gap-2" id="txn-comment-box">
              <Textarea data-testid="comment-input" value={text} onChange={e=>setText(e.target.value)} rows={2}
                        placeholder={paymentLocked
                          ? "Ask a question, or tell us what needs correcting…"
                          : "Write a message to our team…"}/>
              <Button data-testid="comment-send" onClick={post} className="bg-[#CBA96C] text-[#080C16] hover:bg-[#DBC190]">Send</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Read-only. The customer sees exactly what admin has prepared — including edits admin
          made after a call or message — but can never change it: their own fields locked at
          payment and corrections travel as messages (LIFECYCLE.md "Who can edit the document,
          and when"). The stamp header is a preview of the printed sheet, not part of the
          saved document. */}
      <Dialog open={docOpen} onOpenChange={setDocOpen}>
        <DialogContent data-testid="customer-doc-view" className="max-w-[920px] w-[96vw] max-h-[94vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="font-serif-display flex items-center gap-2">
              Your Document
              <span className="text-xs font-normal text-[#6B7280] inline-flex items-center gap-1.5">
                <Lock className="w-3 h-3"/>View only — ask us on the thread to change anything
              </span>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto bg-[#E9EAEC] -mx-6 px-6 py-6 flex-1">
            {docBusy && <div className="p-10 text-center text-sm text-[#6B7280]">Loading your document…</div>}
            {!docBusy && docView?.body && (
              <DocumentSheet body={docView.body} extraClauses={docView.extra_clauses}/>
            )}
            {!docBusy && !docView?.body && (
              <div className="p-10 text-center text-sm text-[#6B7280]">
                Your document is still being prepared. We'll let you know as soon as it's ready.
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
